import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Form,
  Button,
  ListGroup,
  InputGroup,
  Spinner,
  Badge,
} from "react-bootstrap";
import { ArrowLeft, Search, Send, MessageCircle, User } from "lucide-react";
import axios from "axios";
import { useUser } from "../../contextProviders/userContext";
import AppShell from "../../components/AppShell";
import { useLocation } from "react-router-dom";
import { API_BASE_URL } from "../../api";
import "./DirectMessages.css";

const getAuthHeader = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getUserDisplayName = (person) => {
  if (!person) return "Unknown user";
  return (
    person.organizationName ||
    `${person.name || ""} ${person.surname || ""}`.trim() ||
    "Unknown user"
  );
};

const getUserFirstLetter = (person) => {
  const name = person?.name || person?.organizationName || "U";
  return String(name).charAt(0).toUpperCase();
};

const DirectMessages = () => {
  const { user } = useUser();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [contactsError, setContactsError] = useState("");
  const [isSending, setIsSending] = useState(false);

  const messagesEndRef = useRef(null);

  // Fetch conversations list
  const fetchConversations = async (selectUserId = null) => {
    try {
      setLoadingConvs(true);
      const res = await axios.get(`${API_BASE_URL}/messages/conversations`, {
        headers: getAuthHeader(),
      });

      const directConversations = (res.data || []).filter(
        (conversation) =>
          conversation?.type === "direct" &&
          conversation?.otherUser &&
          (conversation.otherUser._id || conversation.otherUser.id),
      );
      setConversations(directConversations);

      // If a selectUserId is provided, try to find and set it as active
      if (selectUserId) {
        const found = directConversations.find(
          (conversation) =>
            conversation?.otherUser?._id === selectUserId ||
            conversation?.otherUser?.id === selectUserId,
        );
        if (found) {
          setActiveChat(found.otherUser);
        } else {
          // If not in active conversations yet, fetch user details to start a new chat
          try {
            const userRes = await axios.get(
              `${API_BASE_URL}/users?search=${selectUserId}`,
              {
                headers: getAuthHeader(),
              },
            );
            const specificUser = (userRes.data || []).find(
              (userRecord) =>
                userRecord._id === selectUserId ||
                userRecord.id === selectUserId,
            );
            if (specificUser) {
              setActiveChat(specificUser);
            }
          } catch (err) {
            console.error("Error fetching specific user details:", err);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
      if (error?.response?.status === 401) {
        setConversations([]);
        setActiveChat(null);
      }
    } finally {
      setLoadingConvs(false);
    }
  };

  const fetchContacts = useCallback(async () => {
    try {
      setLoadingContacts(true);
      setContactsError("");
      const res = await axios.get(`${API_BASE_URL}/users`, {
        headers: getAuthHeader(),
      });
      setContacts(res.data || []);
    } catch (error) {
      console.error("Error fetching message contacts:", error);
      setContactsError("We could not load your message contacts.");
    } finally {
      setLoadingContacts(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const userIdFromUrl = queryParams.get("userId");
    fetchConversations(userIdFromUrl);
  }, [location.search]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    if (!activeChat?._id) return;
    const contact = contacts.find((item) => item._id === activeChat._id);
    if (contact && contact.contactType !== activeChat.contactType) {
      setActiveChat((current) => ({ ...current, ...contact }));
    }
  }, [activeChat?._id, activeChat?.contactType, contacts]);

  // Fetch conversation messages
  const fetchMessages = async (otherUserId) => {
    if (!otherUserId) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/messages/${otherUserId}`, {
        headers: getAuthHeader(),
      });
      setMessages(res.data || []);
    } catch (error) {
      console.error("Error fetching messages:", error);
      if (error?.response?.status === 401) {
        setMessages([]);
      }
    }
  };

  // Poll for messages in the active chat every 4 seconds
  useEffect(() => {
    if (!activeChat) return;

    fetchMessages(activeChat._id);
    const intervalId = setInterval(() => {
      fetchMessages(activeChat._id);
    }, 4000);

    return () => clearInterval(intervalId);
  }, [activeChat]);

  // Scroll to bottom of chat
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Handle User Search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/users?search=${searchQuery}`,
          {
            headers: getAuthHeader(),
          },
        );
        // Filter out self
        const filtered = (res.data || []).filter(
          (u) => u._id !== user?.id && u._id !== user?._id,
        );
        setSearchResults(filtered);
      } catch (err) {
        console.error("Error searching users:", err);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputText.trim() || !activeChat || isSending) return;

    try {
      setIsSending(true);
      const res = await axios.post(
        `${API_BASE_URL}/messages`,
        {
          recipientId: activeChat._id,
          text: inputText.trim(),
        },
        { headers: getAuthHeader() },
      );

      setMessages((prev) => [...prev, res.data]);
      setInputText("");
      // Refresh conversations list to update last message/order
      fetchConversations();
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setIsSending(false);
    }
  };

  const startChatWithUser = (targetUser) => {
    if (!targetUser?._id) return;
    setActiveChat(targetUser);
    setSearchQuery("");
    setSearchResults([]);

    // Add to conversations locally if not present
    const exists = conversations.some(
      (conversation) =>
        conversation?.otherUser?._id === targetUser._id ||
        conversation?.otherUser?.id === targetUser._id,
    );
    if (!exists) {
      setConversations((prev) => [
        {
          type: "direct",
          otherUser: targetUser,
          lastMessage: { text: "No messages yet", createdAt: new Date() },
          unreadCount: 0,
        },
        ...prev,
      ]);
    }
  };

  const getContactType = (contact) => {
    if (contact?.contactType === "administrator" || contact?.role === "admin") {
      return "Administrator";
    }
    if (contact?.contactType === "project_leader") return "Project leader";
    if (contact?.contactType === "project_member") return "Project member";
    if (contact?.role === "organization") return "Project organisation";
    return "Volunteer";
  };

  const getContactContext = (contact) => {
    const projectNames = (contact?.sharedProjects || [])
      .map((project) => project.name)
      .filter(Boolean);
    return projectNames.length
      ? projectNames.join(", ")
      : getContactType(contact);
  };

  const contactDetailsById = new Map(
    contacts.map((contact) => [contact._id, contact]),
  );
  const availableContacts = contacts;

  const cardStyle = {
    backgroundColor: "#fff",
    borderRadius: "16px",
    border: "1px solid #E5E7EB",
    boxShadow: "none",
    overflow: "hidden",
  };

  const activeUserStyle = (convPartner) => {
    const isActive = activeChat && activeChat._id === convPartner._id;
    return {
      cursor: "pointer",
      backgroundColor: isActive ? "#F3F4F6" : "transparent",
      transition: "background-color 0.2s ease",
      padding: "16px",
    };
  };

  return (
    <AppShell>
      <div className="direct-messages">
        <Row className="direct-messages-layout g-3">
          {/* Left panel: Conversations List */}
          <Col
            lg={4}
            className={`direct-messages-list h-100${activeChat ? " is-hidden-mobile" : ""}`}>
            <Card className="h-100" style={cardStyle}>
              <Card.Header className="bg-white border-0 pt-4 px-4 pb-2">
                <h5 className="fw-bold mb-3" style={{ color: "#1B3A87" }}>
                  Consultations
                </h5>
                {/* Search Bar */}
                <div className="position-relative">
                  <InputGroup
                    style={{ borderRadius: "10px", overflow: "hidden" }}>
                    <InputGroup.Text className="bg-light border-0">
                      <Search size={18} className="text-muted" />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder={
                        user?.role === "admin"
                          ? "Search users..."
                          : "Search admins or project leaders..."
                      }
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="bg-light border-0 py-2.5"
                    />
                  </InputGroup>

                  {/* Search Results Dropdown */}
                  {searchResults.length > 0 && (
                    <Card
                      className="position-absolute w-100 shadow-lg mt-1"
                      style={{
                        zIndex: 1000,
                        maxHeight: "250px",
                        overflowY: "auto",
                      }}>
                      <ListGroup variant="flush">
                        {searchResults.map((su) => (
                          <ListGroup.Item
                            key={su._id}
                            action
                            onClick={() => startChatWithUser(su)}
                            className="d-flex align-items-center gap-3 py-3">
                            <div
                              className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                              style={{ width: "40px", height: "40px" }}>
                              <User size={18} className="text-secondary" />
                            </div>
                            <div className="text-start">
                              <div className="fw-bold">
                                {su.organizationName ||
                                  `${su.name} ${su.surname || ""}`.trim()}
                              </div>
                              <small className="text-muted">
                                {getContactType(su)}
                                {su.sharedProjects?.length
                                  ? ` · ${getContactContext(su)}`
                                  : ""}
                              </small>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </Card>
                  )}
                </div>
              </Card.Header>

              {/* Conversations Scroller */}
              <Card.Body
                className="p-0 overflow-auto"
                style={{ height: "calc(100% - 130px)" }}>
                {loadingConvs ? (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : (
                  <>
                    <div className="direct-messages-section-label">
                      Conversations
                    </div>
                    {conversations.length === 0 ? (
                      <div className="direct-messages-empty-list">
                        <MessageCircle size={28} aria-hidden="true" />
                        <span>
                          No conversations yet. Choose a contact below to start
                          one.
                        </span>
                      </div>
                    ) : (
                      <ListGroup variant="flush">
                        {conversations.map((conv) => {
                          const partner = conv?.otherUser;
                          if (!partner?._id && !partner?.id) return null;

                          const contactDetails = contactDetailsById.get(
                            partner._id || partner.id,
                          );
                          return (
                            <ListGroup.Item
                              key={partner._id || partner.id}
                              style={activeUserStyle(partner)}
                              onClick={() =>
                                setActiveChat({ ...partner, ...contactDetails })
                              }
                              className="border-0 border-bottom">
                              <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center gap-3 min-w-0">
                                  <div className="direct-messages-avatar">
                                    {getUserFirstLetter(partner)}
                                  </div>
                                  <div className="text-start min-w-0">
                                    <h6 className="fw-bold mb-1 text-dark text-truncate">
                                      {getUserDisplayName(partner)}
                                    </h6>
                                    <div className="text-muted small text-truncate">
                                      {conv.lastMessage?.text ||
                                        "No messages yet"}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-end">
                                  {Number(conv.unreadCount || 0) > 0 && (
                                    <Badge
                                      bg="danger"
                                      pill
                                      className="px-2 py-1 small">
                                      {conv.unreadCount}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </ListGroup.Item>
                          );
                        })}
                      </ListGroup>
                    )}

                    <div className="direct-messages-section-label direct-messages-section-label--contacts">
                      People you can message
                    </div>
                    {loadingContacts ? (
                      <div className="direct-messages-contacts-state">
                        <Spinner animation="border" size="sm" /> Loading
                        contacts…
                      </div>
                    ) : contactsError ? (
                      <div className="direct-messages-contacts-state text-danger">
                        {contactsError}
                      </div>
                    ) : availableContacts.length === 0 ? (
                      <div className="direct-messages-contacts-state">
                        No message contacts are available yet.
                      </div>
                    ) : (
                      <ListGroup variant="flush">
                        {availableContacts.map((contact) => (
                          <ListGroup.Item
                            key={contact._id}
                            action
                            onClick={() => startChatWithUser(contact)}
                            className="direct-messages-contact">
                            <div className="direct-messages-avatar">
                              {(contact.name || "U").charAt(0).toUpperCase()}
                            </div>
                            <div className="direct-messages-contact__copy">
                              <strong>
                                {contact.organizationName ||
                                  `${contact.name} ${contact.surname || ""}`.trim()}
                              </strong>
                              <span>{getContactType(contact)}</span>
                              {contact.sharedProjects?.length ? (
                                <small>{getContactContext(contact)}</small>
                              ) : null}
                            </div>
                            <span className="direct-messages-contact__action">
                              Message
                            </span>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Right panel: Messages Thread */}
          <Col
            lg={8}
            className={`direct-messages-thread h-100${!activeChat ? " is-hidden-mobile" : ""}`}>
            <Card className="h-100" style={cardStyle}>
              {activeChat ? (
                <>
                  {/* Chat Partner Header */}
                  <Card.Header className="bg-white border-bottom py-3 px-4 d-flex align-items-center gap-3">
                    <Button
                      type="button"
                      variant="link"
                      className="direct-messages-back"
                      aria-label="Back to conversations"
                      onClick={() => setActiveChat(null)}>
                      <ArrowLeft size={20} />
                    </Button>
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white shadow-sm"
                      style={{
                        width: "45px",
                        height: "45px",
                        backgroundColor:
                          activeChat.role === "admin" ? "#1B3A87" : "#1B3A87",
                      }}>
                      {getUserFirstLetter(activeChat)}
                    </div>
                    <div className="text-start">
                      <h6 className="fw-bold mb-0 text-dark">
                        {getUserDisplayName(activeChat)}
                      </h6>
                      <small className="text-muted">
                        {getContactType(activeChat)}
                        {activeChat.sharedProjects?.length
                          ? ` · ${getContactContext(activeChat)}`
                          : ""}
                      </small>
                    </div>
                  </Card.Header>

                  {/* Messages Bubble Container */}
                  <Card.Body
                    className="bg-light p-4 overflow-auto"
                    style={{ height: "calc(100% - 150px)" }}>
                    <div className="d-flex flex-column gap-3">
                      {messages.length === 0 ? (
                        <div className="text-center py-5 text-muted small">
                          This is the beginning of your consultation with{" "}
                          {activeChat.name}.
                        </div>
                      ) : (
                        messages.map((msg) => {
                          const senderId =
                            typeof msg.sender === "object"
                              ? msg.sender?._id || msg.sender?.id
                              : msg.sender;
                          const isSelf =
                            senderId === user?.id ||
                            senderId === user?._id ||
                            senderId === user?.id ||
                            senderId === user?._id;
                          return (
                            <div
                              key={msg._id}
                              className={`d-flex ${isSelf ? "justify-content-end" : "justify-content-start"}`}>
                              <div
                                className="direct-message-bubble"
                                style={{
                                  borderRadius: "16px",
                                  padding: "12px 16px",
                                  backgroundColor: isSelf ? "#1B3A87" : "#fff",
                                  color: isSelf ? "#fff" : "#1F2937",
                                  boxShadow: "none",
                                  textAlign: "left",
                                }}>
                                <div
                                  style={{
                                    fontSize: "0.95rem",
                                    whiteSpace: "pre-wrap",
                                  }}>
                                  {msg.text}
                                </div>
                                <div
                                  className={`text-end mt-1 ${isSelf ? "text-white-50" : "text-muted"}`}
                                  style={{ fontSize: "0.7rem" }}>
                                  {new Date(msg.createdAt).toLocaleTimeString(
                                    [],
                                    { hour: "2-digit", minute: "2-digit" },
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  </Card.Body>

                  {/* Messages Input Box */}
                  <Card.Footer className="bg-white border-0 p-3">
                    <Form onSubmit={handleSendMessage} className="d-flex gap-2">
                      <Form.Control
                        type="text"
                        placeholder="Type your consultation message here..."
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        disabled={isSending}
                        style={{ borderRadius: "10px", padding: "12px" }}
                      />
                      <Button
                        type="submit"
                        disabled={!inputText.trim() || isSending}
                        style={{
                          backgroundColor: "#1B3A87",
                          border: "none",
                          borderRadius: "10px",
                          width: "50px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}>
                        <Send size={18} color="#fff" />
                      </Button>
                    </Form>
                  </Card.Footer>
                </>
              ) : (
                <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted p-5">
                  <MessageCircle
                    size={60}
                    className="mb-3 opacity-30"
                    style={{ color: "#1B3A87" }}
                  />
                  <h5 className="fw-bold">Your Direct Consultations</h5>
                  <p
                    className="small text-center"
                    style={{ maxWidth: "320px" }}>
                    Select an active conversation from the sidebar list, or
                    search for a user to start a consultation.
                  </p>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </AppShell>
  );
};

export default DirectMessages;
